import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import {Transition} from 'react-transition-group';

import {BasicIconButton} from '@pb/kit-component-icon-button';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {
    DURATION_EXIT,
    INDENT_BETWEEN_ITEM,
    Status,
} from '../constants';
import styles from '../push-notification.module.styl';

import type {OnClose, OnCloseAll, NotificationItem as Props} from '../typedef';
import type {FC} from 'react';

const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

const TIMEOUT = {
    appear: 0,
    enter: 0,
    exit: DURATION_EXIT,
};

type Timeout = ReturnType<typeof setTimeout>;

export const PushNotificationItem: FC<Props> = memo(function PushNotificationItem(props: Props) {
    const {
        id,
        body,
        message,
        onCloseNote,
        onCloseAllNote,
        status = Status.DEFAULT,
        autoHideTimeout = 0,
        isNeedClosed,
        buttonProps,
        itemWrapperProps,
        ...transitionProps
    } = props;
    const [loader, setLoader] = useState<boolean>(false);
    const timeoutRef = useRef<Timeout | null>(null);
    const height = useRef<string>('auto');
    const nodeRef = useRef<HTMLDivElement>(null);

    const _onClose: OnClose = async () => {
        const syntheticEvent = new MouseEvent('click') as unknown as React.MouseEvent<HTMLButtonElement>;
        const callback = buttonProps?.onClick?.(syntheticEvent);

        // backward compatibility Remove when handler props will not be used anymore.
        const handlerCallback = typeof buttonProps?.handler === 'function'
            ? buttonProps?.handler?.(undefined, syntheticEvent)
            : null;

        if (callback) {
            setLoader(true);
            await callback;
            setLoader(false);
        } else if (handlerCallback) {
            // backward compatibility Remove when handler props will not be used anymore.
            setLoader(true);
            await handlerCallback;
            setLoader(false);
        }
    };

    const onClose: OnClose = async () => {
        await _onClose();
        await onCloseNote(id);
    };

    const onCloseAll: OnCloseAll = async () => {
        await _onClose();
        await onCloseAllNote();
    };

    const onEnteringCallback = useCallback((): void => {
        const element = nodeRef.current;
        if (element) {
            height.current = `${element.clientHeight + INDENT_BETWEEN_ITEM}px`;
        }
    }, []);

    const onExitCallback = (): void => {
        height.current = '0px';
    };

    useEffect(() => {
        if (autoHideTimeout) {
            timeoutRef.current = setTimeout(onClose, autoHideTimeout);

            return () => {
                if (typeof timeoutRef?.current === 'number') {
                    clearTimeout(timeoutRef?.current);
                }
            };
        }
    }, [autoHideTimeout]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isNeedClosed) {
            onClose();
        }
    }, [isNeedClosed]); // eslint-disable-line react-hooks/exhaustive-deps

    const button = (
        <BasicIconButton
            aria-label="Dismiss"
            {...{...buttonProps, loader, className: 'push-notification-item__icon', onClick: onClose}}
        />
    );

    const containerClasses = classNames('push-notification-item__container', {
        'push-notification-item__container_default': status === Status.DEFAULT,
        'push-notification-item__container_error': status === Status.ERROR,
        'push-notification-item__container_success': status === Status.SUCCESS,
        'push-notification-item__container_warning': status === Status.WARNING,
    });

    const itemClasses = classNames('push-notification-item', itemWrapperProps?.className);
    const notificationRole = status === Status.ERROR ? 'alert' : 'status';

    return (
        <Transition
            nodeRef={nodeRef}
            timeout={TIMEOUT}
            appear={true}
            unmountOnExit={true}
            onEntering={onEnteringCallback}
            onExit={onExitCallback}
            {...transitionProps}>
            {state => (
                <div
                    {...itemWrapperProps}
                    ref={nodeRef}
                    id={`push-notification-${id}`}
                    role={notificationRole}
                    className={itemClasses}
                    style={{height: height.current}}>
                    <div className={classNames(containerClasses, state)}>
                        <div className={classes['push-notification-item__content']}>
                            {body ? body({onClose, onCloseAll}) : <span>{message}</span>}
                            {buttonProps ? button : null}
                        </div>
                    </div>
                </div>
            )}
        </Transition>
    );
});

PushNotificationItem.displayName = 'PushNotificationItem';
